import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Image as ImageIcon, 
  FileText, 
  Users, 
  Newspaper, 
  GalleryVertical, 
  PhoneCall, 
  LogOut, 
  Menu, 
  X, 
  Bell,
  Search,
  User,
  Percent,
  PiggyBank,
  Briefcase,
  TrendingUp,
  Building
} from 'lucide-react';
import { auth } from '../Firebase';
import { signOut } from 'firebase/auth';
import Swal from 'sweetalert2';
import finalLogo from '../assets/logo/final-logo.png';

const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Banner Manager', path: '/admin/banners', icon: ImageIcon },
    { name: 'Growth Stats', path: '/admin/stats', icon: TrendingUp },
    { name: 'Manage Loans', path: '/admin/manage-loans', icon: FileText },
    { name: 'Loan Inquiries', path: '/admin/loans', icon: FileText },
    { name: 'Deposit Schemes', path: '/admin/deposit-schemes', icon: PiggyBank },
    { name: 'Member Manager', path: '/admin/members', icon: Users },
    { name: 'News Manager', path: '/admin/news', icon: Newspaper },
    { name: 'Job Postings', path: '/admin/jobs', icon: Briefcase },
    { name: 'Gallery Manager', path: '/admin/gallery', icon: GalleryVertical },
    { name: 'Manage Branch', path: '/admin/manage-branches', icon: Building },
    { name: 'Contact Inquiries', path: '/admin/contacts', icon: PhoneCall },
  ];

  const handleLogout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You will be logged out of the admin panel!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0D47A1',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Logout!'
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('admin_last_active');
        localStorage.removeItem('admin_last_active_write');
        signOut(auth).then(() => {
          navigate('/admin/login');
        });
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex font-poppins">
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full bg-primary-dark text-white transition-all duration-300 flex flex-col z-30 ${
          isSidebarOpen ? 'w-72' : 'w-20'
        }`}
      >
        <div className="p-6 flex flex-col items-center justify-between flex-shrink-0 border-b border-white/5 relative">
          {isSidebarOpen ? (
            <div className="flex flex-col items-center w-full mt-2">
              <button 
                onClick={() => setSidebarOpen(false)}
                className="absolute top-0 right-0 p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
              
              <div className="flex flex-col items-center text-center mt-4">
                <img 
                  src={finalLogo} 
                  alt="Sangram Logo" 
                  className="w-28 h-28 object-contain rounded-full bg-white p-1.5 shadow-lg hover:scale-105 transition-transform duration-300"
                />
                <span className="font-extrabold text-base tracking-wider uppercase mt-4 block text-secondary">
                  Sangram Admin
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 w-full">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <Menu className="w-6 h-6" />
              </button>
              <img 
                src={finalLogo} 
                alt="Sangram Logo" 
                className="w-8 h-8 object-contain rounded-full bg-white p-0.5 shadow-md"
              />
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto mt-4 px-4 pb-8 space-y-2 sidebar-scroll">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-4 p-4 rounded-2xl transition-all group ${
                location.pathname === item.path 
                ? 'bg-secondary text-primary-dark font-bold shadow-lg shadow-secondary/20' 
                : 'hover:bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              <item.icon className={`w-6 h-6 flex-shrink-0 ${location.pathname === item.path ? 'text-primary-dark' : 'group-hover:text-secondary'}`} />
              <span className={`transition-all duration-300 ${!isSidebarOpen && 'opacity-0 invisible'}`}>
                {item.name}
              </span>
            </Link>
          ))}

          <button
            onClick={handleLogout}
            className="flex items-center gap-4 p-4 rounded-2xl transition-all group text-gray-400 hover:text-red-400 hover:bg-red-400/10 w-full mt-10"
          >
            <LogOut className="w-6 h-6 flex-shrink-0 group-hover:scale-110 transition-transform" />
            <span className={`transition-all duration-300 ${!isSidebarOpen && 'opacity-0 invisible'}`}>
              Logout
            </span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main 
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? 'ml-72' : 'ml-20'
        }`}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-100 sticky top-0 px-8 py-4 flex items-center justify-between z-20">
          <div className="flex items-center gap-4 bg-gray-50 px-4 py-2.5 rounded-2xl w-96 border border-gray-100 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Search className="w-5 h-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search data, reports, etc..." 
              className="bg-transparent border-none outline-none w-full text-sm text-gray-700"
            />
          </div>

          <div className="flex items-center gap-6">
            <button className="relative p-2.5 bg-gray-50 text-gray-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all border border-gray-100 group">
              <Bell className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" />
            </button>
            <div className="h-10 w-px bg-gray-100" />
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="text-right">
                <p className="text-sm font-bold text-gray-800 leading-tight">Admin User</p>
                <p className="text-[10px] font-bold text-secondary uppercase tracking-widest">Main Office</p>
              </div>
              <div className="w-11 h-11 bg-primary text-white rounded-xl flex items-center justify-center font-bold text-lg border-2 border-gray-50 shadow-md group-hover:scale-105 transition-transform">
                <User className="w-6 h-6" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-10 admin-content-container">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
